import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Space, Spin } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';

import {
  Button,
  Form,
  FormItemTextArea,
  Input,
  Modal,
} from '../../../../components/index';
import {
  useCreateDockingObservationMutation,
  useDeleteDockingObservationMutation,
  useGetDockingObservationsQuery,
  useUpdateDockingObservationMutation,
} from '../../../../services/dockingApi';
import {
  DockingObservationAuthorType,
  DockingObservationType,
  StopoverGroupbyDockingStatisticsType,
} from '../../../../types';
import { createDateStringPtBr } from '../../../../utils/utils';

const { confirm } = Modal;
const { TextArea } = Input;

export const OBSERVATION_MAX_LENGTH = 200;

// Mesma regra na criação e na edição: texto obrigatório, espaços não contam.
function isValidObservation(text: string) {
  return text.trim().length > 0 && text.length <= OBSERVATION_MAX_LENGTH;
}

function authorName(user?: DockingObservationAuthorType) {
  if (!user) return 'Autor não identificado';
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.username;
}

const ObservationsHistory = styled.div`
  margin-bottom: 16px;
  max-height: 320px;
  overflow-y: auto;

  .observation-item {
    border-bottom: 1px solid var(--neutral_lighter);
    padding: 8px 0;

    &:last-child {
      border-bottom: 0;
    }
  }

  .observation-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .observation-author {
    color: var(--neutral_medium);
    font-size: 12px;
  }

  .observation-text {
    margin: 4px 0 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .observation-empty {
    color: var(--neutral_medium);
    font-style: italic;
  }
`;

type DockingObservationsModalProps = {
  isVisible: boolean;
  onClose: () => void;
  docking?: StopoverGroupbyDockingStatisticsType;
};

export function DockingObservationsModal(props: DockingObservationsModalProps) {
  const { isVisible, onClose, docking } = props;

  const dockingId = docking?.docking__id;

  const [form] = Form.useForm();
  const [newObservation, setNewObservation] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  const { data: observations, isFetching } = useGetDockingObservationsQuery(
    dockingId as number,
    { skip: !isVisible || !dockingId }
  );

  const [createObservation, { isLoading: isCreating }] =
    useCreateDockingObservationMutation();
  const [updateObservation, { isLoading: isUpdating }] =
    useUpdateDockingObservationMutation();
  const [deleteObservation, { isLoading: isDeleting }] =
    useDeleteDockingObservationMutation();

  const isSaving = isCreating || isUpdating || isDeleting;

  function resetAndClose() {
    form.resetFields();
    setNewObservation('');
    setEditingId(null);
    setEditingText('');
    onClose();
  }

  async function onCreateObservation() {
    if (!dockingId || !isValidObservation(newObservation)) return;
    try {
      await createObservation({
        docking_id: dockingId,
        observation: newObservation.trim(),
      }).unwrap();
      form.resetFields();
      setNewObservation('');
    } catch (error) {
      // O ErrorMiddleware já avisou; aqui só preservamos o texto digitado para
      // o usuário tentar de novo sem reescrever a nota.
    }
  }

  function onStartEditing(observation: DockingObservationType) {
    setEditingId(observation.id);
    setEditingText(observation.observation);
  }

  function onCancelEditing() {
    setEditingId(null);
    setEditingText('');
  }

  async function onSaveEditing(observationId: number) {
    if (!dockingId || !isValidObservation(editingText)) return;
    try {
      await updateObservation({
        docking_id: dockingId,
        observation_id: observationId,
        observation: editingText.trim(),
      }).unwrap();
      onCancelEditing();
    } catch (error) {
      // Mantém o item em edição com o texto digitado.
    }
  }

  function onDeleteObservation(observationId: number) {
    confirm({
      title: 'Tem certeza que deseja excluir esta observação?',
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Sim',
      cancelText: 'Não',
      async onOk() {
        if (!dockingId) return;
        try {
          await deleteObservation({
            docking_id: dockingId,
            observation_id: observationId,
          }).unwrap();
        } catch (error) {
          // O ErrorMiddleware já avisou; o histórico segue como estava.
        }
      },
    });
  }

  function renderObservation(observation: DockingObservationType) {
    const isEditing = editingId === observation.id;

    return (
      <div className="observation-item" key={observation.id}>
        <div className="observation-header">
          <span className="observation-author">
            {authorName(observation.user)} em{' '}
            {createDateStringPtBr(observation.date)}
          </span>
          {!isEditing && (
            <Space size={4}>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                disabled={isSaving || editingId !== null}
                onClick={() => onStartEditing(observation)}
                title="Editar observação"
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isSaving || editingId !== null}
                onClick={() => onDeleteObservation(observation.id)}
                title="Excluir observação"
              />
            </Space>
          )}
        </div>
        {isEditing ? (
          <>
            <TextArea
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              maxLength={OBSERVATION_MAX_LENGTH}
              showCount
              rows={3}
            />
            <Space size={8} style={{ marginTop: '8px' }}>
              <Button size="small" onClick={onCancelEditing}>
                Cancelar
              </Button>
              <Button
                size="small"
                type="primary"
                loading={isUpdating}
                disabled={!isValidObservation(editingText) || isUpdating}
                onClick={() => onSaveEditing(observation.id)}
              >
                Salvar
              </Button>
            </Space>
          </>
        ) : (
          <p className="observation-text">{observation.observation}</p>
        )}
      </div>
    );
  }

  return (
    <Modal
      className="TOSPrimaryModal"
      title={`Observações da atracação ${docking?.code || ''}`}
      visible={isVisible}
      width={640}
      onCancel={resetAndClose}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={isFetching} tip="Carregando...">
        <ObservationsHistory>
          {observations && observations.length > 0 ? (
            observations.map(renderObservation)
          ) : (
            <span className="observation-empty">
              {isFetching
                ? ''
                : 'Nenhuma observação registrada para esta atracação.'}
            </span>
          )}
        </ObservationsHistory>
      </Spin>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_changed: unknown, values: unknown) =>
          setNewObservation(
            (values as { observation?: string }).observation || ''
          )
        }
      >
        <FormItemTextArea
          label="Nova observação"
          name="observation"
          placeholder="Digite a observação"
          maxLength={OBSERVATION_MAX_LENGTH}
          rows={3}
        />
      </Form>
      <Space size={12} style={{ justifyContent: 'flex-end', width: '100%' }}>
        <Button onClick={resetAndClose}>Fechar</Button>
        <Button
          type="primary"
          loading={isCreating}
          disabled={!isValidObservation(newObservation) || isSaving}
          onClick={onCreateObservation}
        >
          Salvar
        </Button>
      </Space>
    </Modal>
  );
}
